import { useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger, SplitText, useGSAP);
gsap.defaults({ ease: 'power3.out', duration: 0.9 });

// A fonte troca as medidas do texto; recalcula os gatilhos quando ela carregar.
if (typeof document !== 'undefined' && document.fonts) {
  document.fonts.ready.then(() => ScrollTrigger.refresh());
}

const QUERIES = {
  motion: '(prefers-reduced-motion: no-preference)',
  desktop: '(min-width: 1024px)',
  finePointer: '(hover: hover) and (pointer: fine)',
};

const CLIP_HIDDEN = 'inset(100% 0% 0% 0% round 16px)';
const CLIP_SHOWN = 'inset(0% 0% 0% 0% round 16px)';

export function splitLines(el, onSplit) {
  return SplitText.create(el, {
    type: 'lines',
    mask: 'lines',
    linesClass: 'split-line',
    autoSplit: true,
    onSplit,
  });
}

// Toca uma vez ao entrar na tela. Não usa once:true porque gatilhos que se
// destroem durante o refresh quebram o ScrollTrigger com muitas instâncias.
function onScroll(trigger, start = 'top 88%') {
  // clamp() garante que elementos colados no fim da página também disparem.
  return { trigger, start: `clamp(${start})`, toggleActions: 'play none none none' };
}

export function revealMedia(media, scrollTrigger, delay = 0) {
  const img = media.querySelector('img');
  const tl = gsap.timeline({ delay, scrollTrigger });
  tl.fromTo(
    media,
    { clipPath: CLIP_HIDDEN },
    { clipPath: CLIP_SHOWN, duration: 1.2, ease: 'expo.inOut', clearProps: 'clipPath' },
    0
  );
  if (img) {
    tl.fromTo(img, { scale: 1.3 }, { scale: 1, duration: 1.6, ease: 'expo.out', clearProps: 'transform' }, 0);
  }
  return tl;
}

function countUp(el) {
  const end = parseFloat(el.dataset.count);
  const decimals = Number(el.dataset.decimals || 0);
  const format = (v) => v.toFixed(decimals).replace('.', ',');
  const state = { value: 0 };

  el.textContent = format(0);
  gsap.to(state, {
    value: end,
    duration: 1.8,
    ease: 'power2.out',
    onUpdate: () => { el.textContent = format(state.value); },
    scrollTrigger: onScroll(el, 'top 95%'),
  });

  return () => { el.textContent = format(end); };
}

function magnetic(el, strength = 0.3) {
  const xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
  const yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });

  const move = (e) => {
    const r = el.getBoundingClientRect();
    xTo((e.clientX - (r.left + r.width / 2)) * strength);
    yTo((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const leave = () => { xTo(0); yTo(0); };

  el.addEventListener('pointermove', move);
  el.addEventListener('pointerleave', leave);
  return () => {
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerleave', leave);
  };
}

/*
  Atributos que qualquer seção pode usar:
  data-lines        título entra linha a linha
  data-rise="0.1"   sobe e aparece (valor = atraso em segundos)
  data-media        bloco de imagem revelado de baixo para cima
  data-count="41"   número conta até o valor (data-decimals opcional)
  data-magnetic     botão puxa em direção ao cursor
*/
function animateDefaults(root, conditions) {
  const cleanups = [];
  const all = (selector) => root.querySelectorAll(selector);

  all('[data-lines]').forEach((el) => {
    splitLines(el, (self) =>
      gsap.from(self.lines, {
        yPercent: 110,
        duration: 1.1,
        ease: 'expo.out',
        stagger: 0.09,
        scrollTrigger: onScroll(el),
      })
    );
  });

  all('[data-rise]').forEach((el) => {
    gsap.from(el, {
      autoAlpha: 0,
      y: 28,
      duration: 1,
      delay: Number(el.dataset.rise) || 0,
      scrollTrigger: onScroll(el),
    });
  });

  all('[data-media]').forEach((el) => revealMedia(el, onScroll(el)));
  all('[data-count]').forEach((el) => cleanups.push(countUp(el)));

  if (conditions.finePointer) {
    all('[data-magnetic]').forEach((el) => cleanups.push(magnetic(el)));
  }

  return () => cleanups.forEach((fn) => fn());
}

// Liga as animações da seção só para quem não pediu movimento reduzido.
// Tudo é revertido ao desmontar ou quando a preferência muda.
export function useMotion(setup) {
  const scope = useRef(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add(
      QUERIES,
      (ctx) => {
        const root = scope.current;
        if (!ctx.conditions.motion || !root) return;
        const undoDefaults = animateDefaults(root, ctx.conditions);
        const undoSetup = setup?.(root, ctx.conditions);
        return () => {
          undoDefaults();
          if (typeof undoSetup === 'function') undoSetup();
        };
      }
    );
    return () => mm.revert();
  });

  return scope;
}

export { gsap, ScrollTrigger, SplitText };
